import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletService } from '@/services/wallet.service';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const paymentMethods = [
  { value: 'UPI', label: 'UPI' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer (NEFT/RTGS/IMPS)' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHEQUE', label: 'Cheque' },
];

const bankDetails = {
  accountName: 'Signals Real Estate Pvt Ltd',
  accountNumber: '1234567890123456',
  ifscCode: 'HDFC0001234',
  bankName: 'HDFC Bank',
  branch: 'Mumbai Main Branch',
  upiId: 'signals@upi',
};

/**
 * Format amount in Indian currency format (lakhs/crores)
 */
const formatIndianCurrency = (amount: number): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

export const TopupRequest: React.FC = () => {
  const navigate = useNavigate();

  const [amount, setAmount] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [paymentReference, setPaymentReference] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const quickAmounts = [100, 500, 1000, 2000, 5000];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const numAmount = Number(amount);
    if (!numAmount || numAmount < 100) {
      setError('Minimum topup amount is Rs 100');
      return;
    }

    if (!paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    try {
      setIsSubmitting(true);
      await walletService.requestTopup({
        amount: numAmount,
        payment_method: paymentMethod,
        payment_reference: paymentReference || undefined,
      });
      setSuccess(true);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to submit topup request. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-secondary-900 mb-2">
            Topup Request Submitted
          </h2>
          <p className="text-secondary-600 mb-6">
            Your topup request for {formatIndianCurrency(Number(amount))} has been submitted.
            Our team will verify your payment and credit your wallet within 24 hours.
          </p>
          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => setSuccess(false)}>
              Submit Another
            </Button>
            <Button onClick={() => navigate('/wallet')}>
              Go to Wallet
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/wallet')}
          className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
        >
          <svg
            className="w-5 h-5 text-secondary-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Add Credits</h1>
          <p className="text-secondary-600 mt-1">
            Request a wallet topup
          </p>
        </div>
      </div>

      {/* Topup Form */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Amount (Min. Rs 100)
            </label>
            <Input
              type="number"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={100}
              leftIcon={
                <span className="text-secondary-500 font-medium">Rs</span>
              }
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {quickAmounts.map((quickAmount) => (
                <button
                  key={quickAmount}
                  type="button"
                  onClick={() => setAmount(String(quickAmount))}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                    Number(amount) === quickAmount
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'border-secondary-300 text-secondary-600 hover:bg-secondary-50'
                  }`}
                >
                  {formatIndianCurrency(quickAmount)}
                </button>
              ))}
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Payment Method
            </label>
            <div className="grid grid-cols-2 gap-3">
              {paymentMethods.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setPaymentMethod(method.value)}
                  className={`p-3 text-sm font-medium rounded-lg border text-left transition-colors ${
                    paymentMethod === method.value
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'border-secondary-300 text-secondary-600 hover:bg-secondary-50'
                  }`}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Payment Reference */}
          <div>
            <Input
              label="Payment Reference (Optional)"
              placeholder="UTR number, transaction ID, or cheque number"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              helperText="Provide the reference number to help us identify your payment faster"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button type="submit" fullWidth isLoading={isSubmitting}>
            Submit Topup Request
          </Button>
        </form>
      </Card>

      {/* Payment Instructions */}
      <Card>
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">
          Payment Instructions
        </h3>

        <div className="space-y-4">
          {/* Bank Transfer Details */}
          <div className="bg-secondary-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-secondary-700 mb-3">
              Bank Transfer Details
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-secondary-500">Account Name:</span>
                <span className="font-medium text-secondary-900">{bankDetails.accountName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">Account Number:</span>
                <span className="font-medium text-secondary-900">{bankDetails.accountNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">IFSC Code:</span>
                <span className="font-medium text-secondary-900">{bankDetails.ifscCode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">Bank:</span>
                <span className="font-medium text-secondary-900">{bankDetails.bankName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">Branch:</span>
                <span className="font-medium text-secondary-900">{bankDetails.branch}</span>
              </div>
            </div>
          </div>

          {/* UPI Details */}
          <div className="bg-secondary-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-secondary-700 mb-3">
              UPI Payment
            </h4>
            <div className="flex items-center justify-between">
              <span className="text-secondary-500">UPI ID:</span>
              <span className="font-medium text-secondary-900">{bankDetails.upiId}</span>
            </div>
          </div>

          {/* Important Notes */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-amber-800 mb-2">
              Important Notes
            </h4>
            <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
              <li>Topup requests are verified within 24 hours</li>
              <li>Always include your registered phone number in payment remarks</li>
              <li>For cash payments, contact our office</li>
              <li>Minimum topup amount is Rs 100</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TopupRequest;
